// /var/www/oszczedzam/client/src/components/goals/GoalCard.js
import React, { useState } from 'react';
import { 
  Box, Card, CardContent, Typography, LinearProgress, 
  IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText,
  Grid, Chip, Button, Divider
} from '@mui/material';
import { 
  MoreVert as MoreIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Flag as FlagIcon,
  Category as CategoryIcon
} from '@mui/icons-material';

// Komponent karty celu oszczędnościowego
function GoalCard({ goal, onEdit, onDelete, onAddSavings }) {
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Oblicz procent postępu
  const progress = Math.min(Math.round((goal.currentAmount / goal.targetAmount) * 100), 100);
  
  // Określ kolor paska postępu
  const getProgressColor = (percent) => {
    if (percent >= 100) return 'success';
    if (percent >= 75) return 'info';
    if (percent >= 50) return 'primary';
    if (percent >= 25) return 'warning';
    return 'error';
  };
  
  // Formatuj kwotę
  const formatAmount = (amount) => {
    return amount.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' });
  };
  
  // Formatuj datę
  const formatDate = (dateString) => {
    if (!dateString) return 'Brak daty';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('pl-PL', options);
  };
  
  // Formatuj czas do osiągnięcia celu
  const formatTimeToGoal = () => {
    if (!goal.targetDate) return 'Brak daty docelowej';
    
    const today = new Date();
    const target = new Date(goal.targetDate);
    
    // Jeśli data już minęła
    if (target < today) return 'Termin minął';
    
    const diffTime = Math.abs(target - today);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) {
      return `Zostało ${diffDays} dni`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `Zostało ${months} ${months === 1 ? 'miesiąc' : months < 5 ? 'miesiące' : 'miesięcy'}`;
    } else {
      const years = Math.floor(diffDays / 365);
      const months = Math.floor((diffDays % 365) / 30);
      return `Zostało ${years} ${years === 1 ? 'rok' : years < 5 ? 'lata' : 'lat'}${months > 0 ? ` i ${months} ${months === 1 ? 'miesiąc' : months < 5 ? 'miesiące' : 'miesięcy'}` : ''}`;
    }
  };
  
  // Pobierz kolor priorytetu
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };
  
  // Pobierz etykietę priorytetu
  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return 'Wysoki';
      case 'medium': return 'Średni';
      case 'low': return 'Niski';
      default: return 'Nieokreślony';
    }
  };
  
  // Obsługa menu
  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  
  const handleEditClick = () => {
    handleCloseMenu();
    onEdit(goal);
  };
  
  const handleDeleteClick = () => {
    handleCloseMenu();
    onDelete(goal.id);
  };
  
  const handleAddSavingsClick = () => {
    handleCloseMenu();
    onAddSavings(goal);
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="div">
            {goal.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip 
              size="small" 
              label={getPriorityLabel(goal.priority)} 
              color={getPriorityColor(goal.priority)}
              sx={{ mr: 1 }}
            />
            <IconButton onClick={handleOpenMenu} size="small">
              <MoreIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleEditClick}>
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edytuj</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleAddSavingsClick}>
                <ListItemIcon>
                  <AddCircleOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Dodaj oszczędności</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleDeleteClick}>
                <ListItemIcon>
                  <DeleteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Usuń</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
        
        {/* Informacje o kategorii */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <CategoryIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
          <Typography variant="body2" color="text.secondary">
            {goal.category}
          </Typography>
        </Box>

        {/* Postęp */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2">
            Postęp: {progress}%
          </Typography>
          <Typography variant="body2">
            {formatAmount(goal.currentAmount)} z {formatAmount(goal.targetAmount)}
          </Typography>
        </Box>
        
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          color={getProgressColor(progress)} 
          sx={{ height: 8, borderRadius: 4, mb: 2 }}
        />
        
        {/* Informacje o terminie */}
        {goal.targetDate && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CalendarIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {formatDate(goal.targetDate)} • {formatTimeToGoal()}
            </Typography>
          </Box>
        )}
        
        {/* Opis (jeśli istnieje) */}
        {goal.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            {goal.description}
          </Typography>
        )}

        {/* Przycisk dodawania oszczędności */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button 
            size="small" 
            variant="outlined" 
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => onAddSavings(goal)}
          >
            Dodaj oszczędności
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default GoalCard;